import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { pick } from 'ramda';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { AlertHelpDialog } from '@iris-platform/iris-react-ui';
import { withApollo } from 'react-apollo';
import {
  stylesToPropType,
  handleChange,
} from '../../../utils';
import { GET_TOKEN_EXCHANGE, UPDATE_TOKEN_EXCHANGE } from '../queries';
import Helpfile from '../helpfile.json';

const styles = () => ({
  root: {
    width: '100%',
  },
  saveButton: {
    borderRadius: '0.125em',
    marginTop: '1em',
    width: '5em',
  },
  textField: {
    height: 'auto',
    paddingLeft: '0.5em',
    '&::before, &:hover::before': {
      display: 'none !important',
    },
  },
  icon: {
    // margin: theme.spacing.unit,
    fontSize: 14,
    cursor: 'pointer',
  },
  iconbutton: {
    width: '18px',
    height: '18px',
  },
});

class TokenExchange extends Component {
  constructor(props) {
    super(props);

    this.state = {
      tokenExchange: {
        app_key: props.appKey,
        current_alg: '',
        public_key_text: '',
        unique_id_claim: '',
      },
      pendingChanges: false,
      error: null,
      serverUrlError: null,
      loading: true,
      open: false,
      dialogTitle: '',
      dialogContent: '',
    };
  }

  async componentDidMount() {
    const { appKey, client } = this.props;

    this.setState({ loading: true });

    try {
      const { data: { tokenExchange } } = await client.query({
        query: GET_TOKEN_EXCHANGE,
        variables: { appKey },
      });
      this.setState({
        tokenExchange: {
          ...tokenExchange,
          app_key: appKey,
        },
        loading: false,
      });
    } catch (error) {
      this.setState({ error });
    } finally {
      this.setState({ loading: false });
    }
  }

  handleHelpOpen = (title, content) => {
    this.setState({
      open: true,
      dialogTitle: title,
      dialogContent: content,
    });
  };

  handleHelpClose = () => {
    this.setState({
      open: false,
      dialogTitle: '',
      dialogContent: '',
    });
  };

  saveChanges = async () => {
    const { client, enqueueSnackbar } = this.props;
    const { tokenExchange, pendingChanges } = this.state;

    if (pendingChanges) {
      this.setState({ loading: true });

      try {
        await client.mutate({
          mutation: UPDATE_TOKEN_EXCHANGE,
          variables: {
            tokenExchange: pick(
              ['app_key', 'current_alg', 'public_key_text', 'unique_id_claim'],
              tokenExchange,
            ),
          },
        });
        enqueueSnackbar('Token exchange settings updated', { variant: 'success' });
        this.setState({ loading: false, pendingChanges: false, error: null });
      } catch (error) {
        enqueueSnackbar('Failed to update token exchange settings', { variant: 'error' });
        this.setState({
          error: new Error('Unable to save changes, please try again'),
        });
      } finally {
        this.setState({ loading: false });
      }
    }
  }

  render() {
    const { classes } = this.props;
    const {
      tokenExchange: {
        public_key_text: publicKeyText,
        unique_id_claim: uniqueIdClaim,
      },
      pendingChanges,
      error,
      serverUrlError,
      loading,
      open,
      dialogTitle,
      dialogContent,
    } = this.state;
    const help = Helpfile.auth;

    return (
      <Fragment>
        <Typography
          variant="subheading"
          gutterBottom
        >
          Token Exchange Settings
          <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.tokenExchange.title, help.tokenExchange.content)}>
            <HelpOutlineIcon className={classes.icon} />
          </IconButton>
        </Typography>
        {loading && <LinearProgress />}
        <form>
          <FormControl className={classes.root} error={!!serverUrlError || !!error}>
            <TextField
              label="Public Key"
              type="text"
              multiline
              rows="6"
              value={publicKeyText || ''}
              placeholder={/* eslint-disable-line */
`-----BEGIN PUBLIC KEY-----
aBcDeFGhIjIklMnoPqrsTuVwXYzaBcDeFGhIjIklMnoPqrsTuVwXYzaBcDeFGhIj
IklMnoPqrsTuVwXYzaBcDeFGhIjIklMnoPqrsTuVwXYzaBcDeFGhIjIklMnoPqrs
TuVwXYzaBcDeFGhIjklMnoPqrsTuVwXYzaBcDeFGhIjIklMnoPqrsTuVwX==
-----END PUBLIC KEY-----
`
              }
              margin="normal"
              InputLabelProps={{ shrink: true }}
              onChange={(event) => {
                handleChange(this, ['tokenExchange', 'public_key_text'])(event);
                this.setState({ pendingChanges: true });
              }}
            />

            <TextField
              label="Payload Claim Uniquely Identifying User"
              value={uniqueIdClaim || ''}
              onChange={(event) => {
                handleChange(this, ['tokenExchange', 'unique_id_claim'])(event);
                this.setState({ pendingChanges: true });
              }}
              type="text"
              InputLabelProps={{ shrink: true }}
              margin="normal"
            />

            <Button
              variant="contained"
              classes={{ root: classes.saveButton }}
              disabled={!pendingChanges}
              onClick={this.saveChanges}
            >
              Save
            </Button>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        </form>
        <AlertHelpDialog
          handleClose={this.handleHelpClose}
          open={open}
          dialogTitle={dialogTitle}
          dialogContent={dialogContent}
        />
      </Fragment>
    );
  }
}

TokenExchange.propTypes = {
  appKey: PropTypes.string.isRequired,
  classes: stylesToPropType(styles()).isRequired,
  client: PropTypes.object.isRequired,
};

export default withSnackbar(withStyles(styles)(withApollo(TokenExchange)));
